(function() {
  'use strict';

  var PAGE_SIZE = 48;
  var FILTER_DEBOUNCE_MS = 120;

  function initCollectionBrowser() {
    var section = document.querySelector('#CollectionSection');
    if (!section) return;

    var displayUrl = (section.getAttribute('data-collection-url') || '').replace(/\/$/, '');
    var sourceUrl = (section.getAttribute('data-source-collection-url') || displayUrl).replace(/\/$/, '');
    var browsingContext = section.getAttribute('data-browsing-context') || 'collection';
    var productAnchor = browsingContext === 'page' ? '#category-page-products' : '';
    var mobileGridQuery = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
    var sortSelect = section.querySelector('[data-sort-by]');
    var allSortOptions = sortSelect ?
      Array.from(sortSelect.options).map(function(option) { return option.cloneNode(true); }) :
      [];
    var activeRequest = null;
    var requestSequence = 0;
    var filterTimer = null;
    var responseCache = new Map();
    var indexCache = new Map();
    var countIndexPromise = null;

    if (!displayUrl || !sourceUrl) return;

    function useMobileGrid() {
      return !!(mobileGridQuery && mobileGridQuery.matches && browsingContext === 'collection');
    }

    function syncResponsiveSortOptions() {
      if (!sortSelect || !allSortOptions.length) return;

      var selectedValue = sortSelect.value ||
        new URLSearchParams(window.location.search).get('sort_by') ||
        'manual';
      var mobile = !!(mobileGridQuery && mobileGridQuery.matches);
      var options = allSortOptions.filter(function(option) {
        return !mobile || option.hasAttribute('data-mobile-sort-label');
      }).map(function(option) {
        var clone = option.cloneNode(true);
        if (mobile) clone.textContent = clone.getAttribute('data-mobile-sort-label');
        return clone;
      });

      sortSelect.replaceChildren.apply(sortSelect, options);
      sortSelect.value = options.some(function(option) { return option.value === selectedValue; }) ?
        selectedValue :
        'manual';
    }

    function beginRequest() {
      if (filterTimer) {
        window.clearTimeout(filterTimer);
        filterTimer = null;
      }
      if (activeRequest && activeRequest.controller) activeRequest.controller.abort();

      activeRequest = {
        id: ++requestSequence,
        controller: typeof AbortController !== 'undefined' ? new AbortController() : null
      };
      return activeRequest;
    }

    function isCurrentRequest(request) {
      return activeRequest && activeRequest.id === request.id;
    }

    function isAbort(error) {
      return error && error.name === 'AbortError';
    }

    function getPrimaryImage(card) {
      return card.querySelector('.custom-product-image-primary, .custom-list-image-primary');
    }

    function watchProductCardImage(card) {
      var image = getPrimaryImage(card);
      var settled = false;
      var resolvePromise;
      var promise = new Promise(function(resolve) {
        resolvePromise = resolve;
      });

      function settle() {
        if (settled) return;
        settled = true;
        if (image) {
          image.removeEventListener('load', settle);
          image.removeEventListener('error', settle);
        }
        card.classList.add('is-image-ready');
        resolvePromise();
      }

      if (!image) {
        settle();
      } else if (image.complete && image.currentSrc) {
        settle();
      } else {
        image.addEventListener('load', settle);
        image.addEventListener('error', settle);
      }

      return { image: image, promise: promise };
    }

    function requestPrimaryImage(image, lazy) {
      if (!image) return;
      if (!lazy && 'loading' in image) image.loading = 'eager';
      if (image.currentSrc || image.getAttribute('src') || image.getAttribute('srcset')) return;

      function setResponsiveSource() {
        var source = image.getAttribute('data-src') || '';
        if (!source) return;
        var widths;
        try {
          widths = JSON.parse(image.getAttribute('data-widths') || '[]');
        } catch (error) {
          widths = [];
        }
        var displayWidth = Math.ceil(Math.max(1, image.getBoundingClientRect().width));
        image.classList.remove('lazyload');
        image.classList.add('lazyautosizes');
        image.setAttribute('sizes', displayWidth + 'px');
        if (widths.length) {
          image.srcset = widths.map(function(width) {
            return source.replace('{width}', width) + ' ' + width + 'w';
          }).join(', ');
        }
        image.src = source.replace('{width}', widths[0] || displayWidth);
      }

      function unveil() {
        if (!window.lazySizes || !window.lazySizes.loader) return false;
        window.lazySizes.loader.unveil(image);
        return true;
      }

      if ('loading' in image) {
        image.loading = lazy ? 'lazy' : 'eager';
        image.decoding = 'async';
        setResponsiveSource();
      } else if (!lazy && !unveil()) {
        setResponsiveSource();
      }
    }

    function primeProductGrid(grid) {
      if (!grid) return Promise.resolve();

      var cards = Array.from(grid.querySelectorAll('[data-product-card]'));
      grid.classList.add('collection-images-managed', 'is-priming-images');

      if (!cards.length) {
        grid.classList.remove('is-priming-images');
        return Promise.resolve();
      }

      var minimumVisibleCards = window.innerWidth <= 768 ? 2 : (window.innerWidth <= 1100 ? 3 : 6);
      var records = cards.map(watchProductCardImage);
      var critical = records.slice(0, minimumVisibleCards);

      critical.forEach(function(record) {
        requestPrimaryImage(record.image);
      });
      if (window.lazySizes && window.lazySizes.loader) {
        window.lazySizes.loader.checkElems();
      }

      return Promise.all(critical.map(function(record) {
        return record.promise;
      })).then(function() {
        return new Promise(function(resolve) {
          window.requestAnimationFrame(function() {
            grid.classList.remove('is-priming-images');
            if (grid.isConnected) {
              records.slice(minimumVisibleCards).forEach(function(record) {
                requestPrimaryImage(record.image, true);
              });
            }
            resolve();
          });
        });
      });
    }

    function setLoading(active, message) {
      var page = document.querySelector('.custom-collection-page');
      var main = section.querySelector('[data-collection-main]');
      var grid = section.querySelector('[data-product-grid]');
      var count = section.querySelector('[data-collection-count]');

      if (page) page.classList.toggle('is-filtering', active);
      if (main) main.setAttribute('aria-busy', active ? 'true' : 'false');

      if (grid) {
        if (active) {
          var height = grid.getBoundingClientRect().height;
          if (height > 0) grid.style.minHeight = Math.ceil(height) + 'px';
        } else {
          grid.style.minHeight = '';
        }
      }

      if (count && message) count.setAttribute('aria-label', message);
    }

    function getSelectedSort() {
      var select = section.querySelector('[data-sort-by]');
      return select ? select.value : 'manual';
    }

    function getCurrentView() {
      if (useMobileGrid()) return 'grid';
      var active = section.querySelector('.change-view.collection-view--active');
      if (active && active.getAttribute('data-view')) return active.getAttribute('data-view');

      var params = new URLSearchParams(window.location.search);
      return browsingContext === 'page'
        ? params.get('product_view') || 'grid'
        : params.get('view') || 'grid';
    }

    function setActiveView(view) {
      if (useMobileGrid()) view = 'grid';
      var interacted = section.getAttribute('data-view-interacted') === 'true';

      section.querySelectorAll('.change-view').forEach(function(link) {
        var active = link.getAttribute('data-view') === view;
        link.classList.toggle('collection-view--active', active);
        link.classList.toggle('is-user-selected', active && interacted);
        if (active) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    function handleizeTag(tag) {
      return tag
        .toString()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/['"]/g, '')
        .replace(/_/g, '_')
        .replace(/[^a-z0-9_]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function getInputHandles(input) {
      var value = input.getAttribute('data-filter-tags') || input.getAttribute('data-filter-handle') || '';
      var seen = {};

      return value.split('|').map(function(tag) {
        return handleizeTag(tag.trim());
      }).filter(function(tag) {
        if (!tag || seen[tag]) return false;
        seen[tag] = true;
        return true;
      });
    }

    function getCheckedFiltersByGroup() {
      var groups = {};

      section.querySelectorAll('.filter-checkbox-input:checked').forEach(function(input) {
        var group = input.getAttribute('data-filter-group') || 'Other';
        var handles = getInputHandles(input);
        if (!handles.length) return;
        if (!groups[group]) groups[group] = [];

        handles.forEach(function(handle) {
          if (groups[group].indexOf(handle) === -1) groups[group].push(handle);
        });
      });

      return groups;
    }

    function getGroupsWithCandidate(candidate) {
      var groups = getCheckedFiltersByGroup();
      var group = candidate.getAttribute('data-filter-group') || 'Other';
      var handles = getInputHandles(candidate);

      if (!groups[group]) groups[group] = [];
      handles.forEach(function(handle) {
        if (groups[group].indexOf(handle) === -1) groups[group].push(handle);
      });
      return groups;
    }

    function getCheckedTagsFlat() {
      var groups = getCheckedFiltersByGroup();
      return Object.keys(groups).reduce(function(tags, group) {
        return tags.concat(groups[group]);
      }, []);
    }

    function getCheckedFilterValues() {
      return Array.from(section.querySelectorAll('.filter-checkbox-input:checked')).map(function(input) {
        return {
          group: input.getAttribute('data-filter-group') || 'Other',
          value: input.value
        };
      });
    }

    function getFilterDisplayLabel(input) {
      var label = input.closest('label');
      var text = label && label.querySelector('.filter-checkbox-text, .color-filter-name');
      return text ? text.textContent.trim() : '';
    }

    function updateFilterUi() {
      var checked = section.querySelectorAll('.filter-checkbox-input:checked');
      var categoryOutput = section.querySelector('[data-active-category]');
      var filterOutput = section.querySelector('[data-active-filters]');
      var categories = [];
      var additional = [];

      section.querySelectorAll('.custom-filter-header [data-filter-clear]').forEach(function(clear) {
        clear.hidden = checked.length === 0;
      });

      checked.forEach(function(input) {
        var label = getFilterDisplayLabel(input);
        var group = (input.getAttribute('data-filter-group') || '').toLowerCase();
        var output = group === 'category' ? categories : additional;
        if (label && output.indexOf(label) === -1) output.push(label);
      });

      if (categoryOutput) {
        categoryOutput.textContent = categories.join(', ');
        categoryOutput.hidden = categories.length === 0;
      }
      if (filterOutput) {
        filterOutput.textContent = additional.join(', ');
        filterOutput.hidden = additional.length === 0;
      }
    }

    function addViewToParams(params, source) {
      var view = getCurrentView();
      params.delete('view');
      params.delete('product_view');

      if (view === 'list') {
        params.set(source || browsingContext === 'collection' ? 'view' : 'product_view', 'list');
      }
    }

    function buildHistoryUrl(pageNumber) {
      var params = new URLSearchParams(window.location.search);
      var tags = getCheckedTagsFlat();
      var sort = getSelectedSort();

      params.delete('filters');
      params.delete('filter_value');
      params.delete('sort_by');
      params.delete('page');
      params.delete('view');
      params.delete('product_view');

      if (tags.length) params.set('filters', tags.join(','));
      getCheckedFilterValues().forEach(function(filter) {
        params.append('filter_value', filter.group + ':' + filter.value);
      });
      if (sort && sort !== 'manual') params.set('sort_by', sort);
      if (pageNumber && String(pageNumber) !== '1') params.set('page', pageNumber);
      addViewToParams(params, false);

      var query = params.toString();
      return (query ? displayUrl + '?' + query : displayUrl) + productAnchor;
    }

    function buildSourceUrl(pageNumber, sectionRequest) {
      var url = new URL(sourceUrl, window.location.origin);
      var sort = getSelectedSort();
      var view = getCurrentView();

      if (sort && sort !== 'manual' && sort !== 'popularity') {
        url.searchParams.set('sort_by', sort);
      }
      if (pageNumber && String(pageNumber) !== '1') {
        url.searchParams.set('page', pageNumber);
      }

      if (sectionRequest) {
        if (view === 'list') url.searchParams.set('view', 'list');
        url.searchParams.set('section_id', 'collection-template');
      } else {
        url.searchParams.set('view', view === 'list' ? 'browser-list' : 'browser-grid');
      }
      return url.pathname + '?' + url.searchParams.toString();
    }

    function getNativeSingleTag() {
      var groups = getCheckedFiltersByGroup();
      var names = Object.keys(groups);
      if (names.length !== 1 || groups[names[0]].length !== 1) return '';
      if (getSelectedSort() === 'popularity') return '';
      return groups[names[0]][0];
    }

    function buildNativeFilterUrl(tag, pageNumber) {
      var url = new URL(sourceUrl + '/' + tag, window.location.origin);
      var sort = getSelectedSort();

      if (sort && sort !== 'manual') url.searchParams.set('sort_by', sort);
      if (pageNumber && String(pageNumber) !== '1') url.searchParams.set('page', pageNumber);
      if (getCurrentView() === 'list') url.searchParams.set('view', 'list');
      url.searchParams.set('section_id', 'collection-template');
      return url.pathname + '?' + url.searchParams.toString();
    }

    function fetchText(url, signal) {
      if (responseCache.has(url)) return Promise.resolve(responseCache.get(url));

      return fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        signal: signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function(response) {
        if (!response.ok) throw new Error('Collection request failed with status ' + response.status);
        return response.text();
      }).then(function(text) {
        responseCache.set(url, text);
        if (responseCache.size > 30) {
          responseCache.delete(responseCache.keys().next().value);
        }
        return text;
      });
    }

    function stripNoscriptFallbacks(html) {
      return html.replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, '');
    }

    function parseDocument(html) {
      var template = document.createElement('template');
      template.innerHTML = stripNoscriptFallbacks(html);
      return template.content;
    }

    function parseIndexResponse(html) {
      var doc = parseDocument(html);
      var data = doc.querySelector('[data-collection-browser-data]');
      if (!data) throw new Error('Collection browser data was missing');

      return {
        products: JSON.parse(data.textContent),
        page: parseInt(data.getAttribute('data-page'), 10) || 1,
        pages: parseInt(data.getAttribute('data-pages'), 10) || 1,
        total: parseInt(data.getAttribute('data-total'), 10) || 0
      };
    }

    function buildCountIndexUrl(page) {
      var url = new URL(sourceUrl, window.location.origin);
      url.searchParams.set('view', 'browser-counts');
      if (page > 1) url.searchParams.set('page', page);
      return url.pathname + '?' + url.searchParams.toString();
    }

    function parseCountIndexResponse(html) {
      var doc = parseDocument(html);
      var data = doc.querySelector('[data-collection-browser-counts]');
      if (!data) throw new Error('Collection count data was missing');
      return {
        products: JSON.parse(data.textContent),
        page: parseInt(data.getAttribute('data-page'), 10) || 1,
        pages: parseInt(data.getAttribute('data-pages'), 10) || 1
      };
    }

    function normalizeProducts(products) {
      products.forEach(function(product) {
        var seen = {};
        product.normalizedTags = (product.tags || []).map(handleizeTag).filter(function(tag) {
          if (!tag || seen[tag]) return false;
          seen[tag] = true;
          return true;
        });
        product.tagSet = new Set(product.normalizedTags);
      });
      return products;
    }

    function loadCountIndex() {
      if (countIndexPromise) return countIndexPromise;

      countIndexPromise = fetchText(buildCountIndexUrl(1)).then(function(html) {
        var first = parseCountIndexResponse(html);
        var pageJobs = [];
        for (var page = 2; page <= first.pages; page++) {
          pageJobs.push(fetchText(buildCountIndexUrl(page)).then(parseCountIndexResponse));
        }
        return Promise.all(pageJobs).then(function(rest) {
          var products = first.products.slice();
          rest.sort(function(a, b) { return a.page - b.page; }).forEach(function(result) {
            products = products.concat(result.products);
          });
          return normalizeProducts(products);
        });
      }).catch(function(error) {
        countIndexPromise = null;
        throw error;
      });
      return countIndexPromise;
    }

    function loadCollectionIndex(request) {
      var cacheKey = getCurrentView() + '|' + getSelectedSort();
      if (indexCache.has(cacheKey)) return Promise.resolve(indexCache.get(cacheKey));

      var signal = request.controller ? request.controller.signal : undefined;
      var firstUrl = buildSourceUrl(1, false);

      return fetchText(firstUrl, signal).then(function(html) {
        var first = parseIndexResponse(html);
        var pageJobs = [];

        for (var page = 2; page <= first.pages; page++) {
          pageJobs.push(fetchText(buildSourceUrl(page, false), signal).then(parseIndexResponse));
        }

        return Promise.all(pageJobs).then(function(rest) {
          var products = first.products.slice();
          rest.sort(function(a, b) { return a.page - b.page; }).forEach(function(result) {
            products = products.concat(result.products);
          });

          products = normalizeProducts(products);
          var index = { products: products, total: first.total };
          indexCache.set(cacheKey, index);
          return index;
        });
      });
    }

    function productMatchesTag(product, tag) {
      return product.tagSet.has(tag);
    }

    function getFilteredProducts(products, groups) {
      var groupNames = Object.keys(groups);
      if (!groupNames.length) return products.slice();

      var groupResults = groupNames.map(function(groupName) {
        var seen = {};
        var merged = [];

        groups[groupName].forEach(function(tag) {
          products.forEach(function(product) {
            if (!seen[product.handle] && productMatchesTag(product, tag)) {
              seen[product.handle] = true;
              merged.push(product);
            }
          });
        });
        return merged;
      });

      var allowedByGroups = groupResults.map(function(group) {
        return new Set(group.map(function(product) { return product.handle; }));
      });
      var seen = {};

      // Filter the sorted source index so OR selections cannot reorder results.
      return products.filter(function(product) {
        if (seen[product.handle]) return false;
        var matches = allowedByGroups.every(function(allowed) {
          return allowed.has(product.handle);
        });
        if (matches) seen[product.handle] = true;
        return matches;
      });
    }

    function updateFilterCounts(products) {
      section.querySelectorAll('.filter-checkbox-input').forEach(function(input) {
        var label = input.closest('label');
        var output = label && label.querySelector('.filter-menu-count');
        if (!output) return;

        var count = getFilteredProducts(products, getGroupsWithCandidate(input)).length;
        output.textContent = count;
        output.setAttribute('aria-label', count + (count === 1 ? ' product' : ' products'));
      });
    }

    function refreshFilterCounts() {
      loadCountIndex().then(updateFilterCounts).catch(function() {
        /* Existing labels remain usable if the optional count request fails. */
      });
    }

    function sortPopularity(products) {
      if (getSelectedSort() !== 'popularity') return products;
      return products.sort(function(a, b) {
        return (parseInt(b.popularity, 10) || 0) - (parseInt(a.popularity, 10) || 0);
      });
    }

    function setGridClass() {
      var grid = section.querySelector('[data-product-grid]');
      if (!grid) return;
      grid.className = getCurrentView() === 'list'
        ? 'custom-product-list category-product-list collection-images-managed is-priming-images'
        : 'grid-uniform custom-product-grid category-product-grid collection-images-managed is-priming-images';
    }

    function renderProducts(products) {
      var grid = section.querySelector('[data-product-grid]');
      if (!grid) return Promise.resolve();
      setGridClass();

      if (!products.length) {
        grid.innerHTML =
          '<div class="custom-empty-collection">' +
            '<span class="custom-empty-collection__icon" aria-hidden="true">⌕</span>' +
            '<h2>No products match these filters</h2>' +
            '<p>Try removing a filter or clearing your selections to see more products.</p>' +
            '<a href="' + displayUrl + '" class="custom-empty-collection__action" data-filter-clear>Clear filters</a>' +
          '</div>';
        grid.classList.remove('is-priming-images');
        return Promise.resolve();
      }

      var template = document.createElement('template');
      template.innerHTML = products.map(function(product) {
        return stripNoscriptFallbacks(product.html);
      }).join('');
      grid.replaceChildren(template.content);
      document.dispatchEvent(new CustomEvent('collection:grid:updated', {
        detail: { grid: grid, count: products.length }
      }));
      return primeProductGrid(grid);
    }

    function updateCount(count, announce) {
      var output = section.querySelector('[data-collection-count]');
      if (!output) return;
      var text = count + (count === 1 ? ' product' : ' products');
      output.textContent = text;
      output.setAttribute('aria-label', announce ? 'Results updated: ' + text : text);
    }

    function paginationParts(current, pages) {
      var parts = [];
      for (var page = 1; page <= pages; page++) {
        if (page === 1 || page === pages || Math.abs(page - current) <= 2) {
          parts.push(page);
        } else if (parts[parts.length - 1] !== null) {
          parts.push(null);
        }
      }
      return parts;
    }

    function escapeAttribute(value) {
      return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    }

    function updateFilteredPagination(current, pages) {
      var output = section.querySelector('[data-collection-pagination]');
      if (!output) return;
      if (pages <= 1) {
        output.innerHTML = '';
        return;
      }

      function link(page, label, rel) {
        var href = escapeAttribute(buildHistoryUrl(page));
        return '<a href="' + href + '"' + (rel ? ' rel="' + rel + '"' : '') +
          ' aria-label="' + escapeAttribute(label) + '">' +
          (label === 'Previous' ? '<span aria-hidden="true">&lsaquo;</span> Previous' :
            label === 'Next' ? 'Next <span aria-hidden="true">&rsaquo;</span>' : page) +
          '</a>';
      }

      var html = '<nav class="pagination-custom-nav" aria-label="Pagination"><ul class="pagination-custom" role="list">';
      html += current > 1
        ? '<li class="pagination-custom__direction">' + link(current - 1, 'Previous', 'prev') + '</li>'
        : '<li class="pagination-custom__direction disabled"><span aria-disabled="true"><span aria-hidden="true">&lsaquo;</span> Previous</span></li>';

      paginationParts(current, pages).forEach(function(page) {
        if (page === null) {
          html += '<li class="pagination-custom__ellipsis"><span aria-hidden="true">&hellip;</span></li>';
        } else if (page === current) {
          html += '<li class="active"><span aria-current="page">' + page + '</span></li>';
        } else {
          html += '<li>' + link(page, 'Page ' + page, '') + '</li>';
        }
      });

      html += current < pages
        ? '<li class="pagination-custom__direction">' + link(current + 1, 'Next', 'next') + '</li>'
        : '<li class="pagination-custom__direction disabled"><span aria-disabled="true">Next <span aria-hidden="true">&rsaquo;</span></span></li>';
      output.innerHTML = html + '</ul></nav>';
    }

    function focusResults(shouldScroll) {
      if (!shouldScroll) return;
      var main = section.querySelector('[data-collection-main]');
      if (!main) return;

      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
      main.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
    }

    function finishRequest(request) {
      if (!isCurrentRequest(request)) return;
      setLoading(false);
      updateFilterUi();
      refreshFilterCounts();
    }

    function clearRequestError() {
      var error = section.querySelector('[data-collection-error]');
      if (!error) return;
      error.textContent = '';
      error.hidden = true;
    }

    function showRequestError(request) {
      if (!isCurrentRequest(request)) return;
      var error = section.querySelector('[data-collection-error]');
      if (!error) return;
      error.textContent = 'Products couldn\u2019t be updated. Please try again.';
      error.hidden = false;
    }

    function renderIndexedCollection(pushState, pageNumber, moveToResults) {
      var request = beginRequest();
      var requestedPage = Math.max(1, parseInt(pageNumber, 10) || 1);
      setLoading(true, 'Updating products');

      loadCollectionIndex(request).then(function(index) {
        if (!isCurrentRequest(request)) return;
        var products = getFilteredProducts(index.products, getCheckedFiltersByGroup());
        products = sortPopularity(products);

        var pages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
        var current = Math.min(requestedPage, pages);
        var start = (current - 1) * PAGE_SIZE;

        return renderProducts(products.slice(start, start + PAGE_SIZE)).then(function() {
          if (!isCurrentRequest(request)) return;
          clearRequestError();
          updateCount(products.length, true);
          updateFilteredPagination(current, pages);

          if (pushState) window.history.pushState({ collectionAjax: true }, '', buildHistoryUrl(current));
          focusResults(moveToResults);
        });
      }).catch(function(error) {
        if (!isAbort(error)) showRequestError(request);
      }).finally(function() {
        finishRequest(request);
      });
    }

    function replaceFromSection(doc) {
      var newSection = doc.querySelector('#CollectionSection');
      var currentGrid = section.querySelector('[data-product-grid]');
      var newGrid = newSection && newSection.querySelector('[data-product-grid]');
      var currentCount = section.querySelector('[data-collection-count]');
      var newCount = newSection && newSection.querySelector('[data-collection-count]');
      var currentPagination = section.querySelector('[data-collection-pagination]');
      var newPagination = newSection && newSection.querySelector('[data-collection-pagination]');

      if (!newSection || !currentGrid || !newGrid) throw new Error('Collection section response was incomplete');
      currentGrid.replaceWith(newGrid);
      if (currentCount && newCount) currentCount.innerHTML = newCount.innerHTML;
      if (currentPagination && newPagination) currentPagination.innerHTML = newPagination.innerHTML;
      document.dispatchEvent(new CustomEvent('collection:grid:updated', {
        detail: { grid: newGrid }
      }));
      return primeProductGrid(newGrid);
    }

    function fetchBaseCollection(pushState, pageNumber, moveToResults) {
      var nativeTag = getNativeSingleTag();
      if (nativeTag) {
        fetchNativeFilteredCollection(nativeTag, pushState, pageNumber, moveToResults);
        return;
      }
      if (getCheckedTagsFlat().length || getSelectedSort() === 'popularity') {
        renderIndexedCollection(pushState, pageNumber, moveToResults);
        return;
      }

      var request = beginRequest();
      var signal = request.controller ? request.controller.signal : undefined;
      var page = Math.max(1, parseInt(pageNumber, 10) || 1);
      setLoading(true, 'Updating products');

      fetchText(buildSourceUrl(page, true), signal).then(function(html) {
        if (!isCurrentRequest(request)) return;
        return replaceFromSection(parseDocument(html)).then(function() {
          if (!isCurrentRequest(request)) return;
          clearRequestError();
          if (pushState) window.history.pushState({ collectionAjax: true }, '', buildHistoryUrl(page));
          updateCount(
            parseInt((section.querySelector('[data-collection-count]') || {}).textContent, 10) || 0,
            true
          );
          focusResults(moveToResults);
        });
      }).catch(function(error) {
        if (!isAbort(error)) showRequestError(request);
      }).finally(function() {
        finishRequest(request);
      });
    }

    function fetchNativeFilteredCollection(tag, pushState, pageNumber, moveToResults) {
      var request = beginRequest();
      var signal = request.controller ? request.controller.signal : undefined;
      var page = Math.max(1, parseInt(pageNumber, 10) || 1);
      setLoading(true, 'Updating products');

      fetchText(buildNativeFilterUrl(tag, page), signal).then(function(html) {
        if (!isCurrentRequest(request)) return;
        return replaceFromSection(parseDocument(html)).then(function() {
          if (!isCurrentRequest(request)) return;
          clearRequestError();
          if (pushState) window.history.pushState({ collectionAjax: true }, '', buildHistoryUrl(page));
          updateCount(
            parseInt((section.querySelector('[data-collection-count]') || {}).textContent, 10) || 0,
            true
          );
          focusResults(moveToResults);
        });
      }).catch(function(error) {
        if (!isAbort(error)) showRequestError(request);
      }).finally(function() {
        finishRequest(request);
      });
    }

    function restoreControlsFromUrl() {
      var params = new URLSearchParams(window.location.search);
      var filterValues = params.getAll('filter_value').map(function(filter) {
        var separator = filter.indexOf(':');
        return {
          group: separator === -1 ? '' : filter.slice(0, separator),
          value: separator === -1 ? filter : filter.slice(separator + 1)
        };
      });
      var filters = (params.get('filters') || '').split(',').filter(Boolean).map(function(tag) {
        return handleizeTag(decodeURIComponent(tag.trim()));
      });
      var sort = params.get('sort_by') || 'manual';
      var view = useMobileGrid() ? 'grid' :
        (browsingContext === 'page' ? params.get('product_view') : params.get('view')) || 'grid';
      var select = section.querySelector('[data-sort-by]');

      if (select) {
        select.value = Array.from(select.options).some(function(option) {
          return option.value === sort;
        }) ? sort : 'manual';
      }
      setActiveView(view);

      section.querySelectorAll('.filter-checkbox-input').forEach(function(input) {
        if (filterValues.length) {
          input.checked = filterValues.some(function(filter) {
            return filter.group === (input.getAttribute('data-filter-group') || 'Other') &&
              filter.value === input.value;
          });
          return;
        }
        input.checked = getInputHandles(input).some(function(handle) {
          return filters.indexOf(handle) !== -1;
        });
      });
      updateFilterUi();
    }

    function scheduleFilterUpdate() {
      if (filterTimer) window.clearTimeout(filterTimer);
      if (activeRequest && activeRequest.controller) activeRequest.controller.abort();
      activeRequest = { id: ++requestSequence, controller: null };
      setLoading(true, 'Updating products');
      filterTimer = window.setTimeout(function() {
        filterTimer = null;
        fetchBaseCollection(true, 1, false);
      }, FILTER_DEBOUNCE_MS);
    }

    document.addEventListener('change', function(event) {
      var filter = event.target.closest('.filter-checkbox-input');
      if (!filter || !section.contains(filter)) return;
      updateFilterUi();
      scheduleFilterUpdate();
    });

    document.addEventListener('change', function(event) {
      var sort = event.target.closest('[data-sort-by]');
      if (!sort || !section.contains(sort)) return;
      fetchBaseCollection(true, 1, false);
    });

    document.addEventListener('click', function(event) {
      var view = event.target.closest('.change-view');
      if (!view || !section.contains(view)) return;
      event.preventDefault();
      section.setAttribute('data-view-interacted', 'true');
      setActiveView(view.getAttribute('data-view') || 'grid');
      fetchBaseCollection(true, 1, false);
    });

    document.addEventListener('click', function(event) {
      var clear = event.target.closest('[data-filter-clear]');
      if (!clear || !section.contains(clear)) return;
      event.preventDefault();

      section.querySelectorAll('.filter-checkbox-input').forEach(function(input) {
        input.checked = false;
      });
      updateFilterUi();
      fetchBaseCollection(true, 1, false);
    });

    document.addEventListener('click', function(event) {
      var link = event.target.closest('[data-collection-pagination] a');
      if (!link || !section.contains(link)) return;
      event.preventDefault();
      if (section.querySelector('[data-collection-main][aria-busy="true"]')) return;

      var page = new URL(link.href, window.location.origin).searchParams.get('page') || 1;
      fetchBaseCollection(true, page, true);
    });

    window.addEventListener('popstate', function() {
      var params = new URLSearchParams(window.location.search);
      restoreControlsFromUrl();
      fetchBaseCollection(false, params.get('page') || 1, false);
    });

    var initialMain = section.querySelector('[data-collection-main]');
    if (initialMain) initialMain.setAttribute('aria-busy', 'true');
    primeProductGrid(section.querySelector('[data-product-grid]')).then(function() {
      if (initialMain && !document.querySelector('.custom-collection-page.is-filtering')) {
        initialMain.setAttribute('aria-busy', 'false');
      }
    });

    syncResponsiveSortOptions();
    restoreControlsFromUrl();
    if (mobileGridQuery) {
      var handleSortBreakpointChange = function() {
        syncResponsiveSortOptions();
        restoreControlsFromUrl();
      };
      if (mobileGridQuery.addEventListener) {
        mobileGridQuery.addEventListener('change', handleSortBreakpointChange);
      } else if (mobileGridQuery.addListener) {
        mobileGridQuery.addListener(handleSortBreakpointChange);
      }
    }
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(refreshFilterCounts, { timeout: 1500 });
    } else {
      window.addEventListener('load', refreshFilterCounts, { once: true });
    }
    var initialParams = new URLSearchParams(window.location.search);
    if (
      initialParams.get('filters') ||
      initialParams.get('sort_by') === 'popularity' ||
      (
        browsingContext === 'page' &&
        (initialParams.get('sort_by') || initialParams.get('page') || initialParams.get('product_view'))
      )
    ) {
      fetchBaseCollection(false, initialParams.get('page') || 1, false);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionBrowser, { once: true });
  } else {
    initCollectionBrowser();
  }
})();
